import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query/react";

import { applyActAsHeader } from "../utils/actAs";
import {
  classifyDemoError,
  clearDemoMode,
  emitDemoBlocked,
  getDemoMode,
  guardDemoRequest,
  isDemoMode,
} from "../utils/demoMode";

export const setAuthTokenToCookies = (token: string): void => {
  // HttpOnlyを削除してJavaScriptから読み取れるようにする（開発環境用）
  // 本番環境では別の認証方法を検討する必要があります
  document.cookie = `auth_token=${token};path=/;max-age=86400;SameSite=Strict`; // Save token with 24-hour expiration
  syncSessionBaseline();
};

export const getAuthTokenFromCookies = (): string | null => {
  const match = document.cookie.match(new RegExp("(^| )auth_token=([^;]+)"));
  return match ? match[2] : null;
};

export const removeAuthTokenFromCookies = (): void => {
  document.cookie = "auth_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT;";
  syncSessionBaseline();
};

// ─── セッション同一性（別タブ / 戻るボタン対策）─────────────────
//
// auth_token Cookie とデモ状態（localStorage）はブラウザ全体で共有されるため、
// 別タブでサインイン・サインアウト・デモ入場が起きると、すでに描画済みの
// 画面だけが古いユーザーのまま取り残される。表示は元のユーザーなのに書き込み
// だけデモガードに遮断される、という混乱がここから生まれる。
//
// そこで「この document が描画に使ったセッション」を指紋として覚えておき、
// SessionSwitchGuard（src/components/session-guard）が変化を監視して読み込み
// 直しを促す。自分の操作でセッションを切り替えた側は指紋を更新するため、
// Cookie 操作の入口である上記 2 関数から syncSessionBaseline() を呼んでいる。
// デモ状態だけを書き換える enterDemoMode / clearDemoMode の直後に Cookie 操作が
// 続かない箇所（DemoEntry）は、明示的に syncSessionBaseline() を呼ぶこと。

let sessionBaseline: string | null = null;

const readSessionFingerprint = (): string =>
  `${getAuthTokenFromCookies() ?? ""}|${getDemoMode()?.token ?? ""}`;

/** 現在のセッションを「この画面が描画に使ったセッション」として記録する */
export const syncSessionBaseline = (): void => {
  sessionBaseline = readSessionFingerprint();
};

/**
 * 記録した基準から変化していれば true。
 * 未記録（SessionSwitchGuard 未マウント）の間は常に false。
 */
export const hasSessionSwitched = (): boolean =>
  sessionBaseline !== null && sessionBaseline !== readSessionFingerprint();

/**
 * CookieからCSRFトークンを取得する
 * @returns CSRFトークン
 */
export const getCsrfTokenFromCookies = (): string | null => {
  const match = document.cookie.match(new RegExp("(^| )csrftoken=([^;]+)"));
  return match ? match[2] : null;
};

/**
 * RTK Query / fetch / `throw new Error(...)` など混在する例外から、
 * ユーザーに見せるメッセージを一貫したルールで抽出する。
 *
 * RTK Query の `unwrap()` が reject する値は `Error` インスタンスではなく
 * `{ status, data }` 形式の `FetchBaseQueryError` なので、`error instanceof
 * Error` で拾うと BE の `data.message`（例: エスクロー残高不足の説明）が
 * 捨てられて汎用文言だけが表示される。catch 節では本関数を使うこと。
 *
 * 優先順位:
 *   1. BE 由来の `err.data.message` / `err.data.detail` / `err.data.error`
 *   2. JS 例外の `err.message`
 *   3. 呼び出し側が指定するフォールバックメッセージ
 */
export const extractErrorMessage = (err: unknown, fallback: string): string => {
  const source = err as
    | {
        data?: { message?: unknown; detail?: unknown; error?: unknown };
        message?: unknown;
      }
    | undefined;

  const candidate =
    source?.data?.message ??
    source?.data?.detail ??
    source?.data?.error ??
    source?.message;

  if (typeof candidate === "string" && candidate.length > 0) {
    return candidate;
  }
  if (candidate !== undefined && candidate !== null) {
    try {
      return JSON.stringify(candidate);
    } catch {
      // noop: JSON にできない値はフォールバックへ
    }
  }
  return fallback;
};

/**
 * 認証・CSRF対応済みの共通baseQueryを作成する
 *
 * デモモード（外部公開向けサンプルサイト）中は書き込みリクエストを
 * 送信前に遮断する。本関数は約40の RTK Query スライスが共用しているため、
 * ここでガードすることで全画面が一括でデモ対応になる。
 * BE 側にも DemoReadOnlyMiddleware の安全網がある（二重ガード）。
 *
 * @param baseUrl - APIのベースURL
 * @returns 設定済みのbaseQuery
 */
export const createAuthBaseQuery = (
  baseUrl: string,
): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> => {
  const rawBaseQuery = fetchBaseQuery({
    baseUrl,
    credentials: "include", // クロスオリジンでクッキー認証を使う場合に必要
    prepareHeaders: (headers) => {
      const token = getAuthTokenFromCookies();
      if (token) {
        headers.set("Authorization", `Token ${token}`);
      }

      const csrfToken = getCsrfTokenFromCookies();
      if (csrfToken) {
        headers.set("X-CSRFToken", csrfToken);
      }

      // 代理操作 (act-as) 中は X-Act-As-User を付与する
      applyActAsHeader(headers);

      return headers;
    },
  });

  return async (args, api, extraOptions) => {
    if (isDemoMode()) {
      const method = typeof args === "string" ? "GET" : (args.method ?? "GET");
      const url = typeof args === "string" ? args : args.url;
      const guard = guardDemoRequest(method, url);

      if (guard.kind === "silent-success") {
        // 既読・DL 記録などの自動書き込みは成功したことにして送信しない。
        // marked は markChatMessagesAsRead の楽観減算（marked 件数分）が
        // 0 になるよう明示する。
        return {
          data: { status: "success", data: { marked: 0, downloaded: 0, demo: true } },
        };
      }
      if (guard.kind === "blocked") {
        emitDemoBlocked();
        return {
          error: {
            status: 403,
            data: {
              status: "error",
              code: "DEMO_READ_ONLY",
              message: "デモモードのため、書き込みはできません。",
            },
          } as FetchBaseQueryError,
        };
      }
    }

    const result = await rawBaseQuery(args, api, extraOptions);
    if (result.error) {
      // BE 安全網（DemoReadOnlyMiddleware）からの応答を処理する。
      // 認証系入口（demo/session・login）は呼び出し元が処理するため対象外。
      const requestUrl = typeof args === "string" ? args : args.url;
      const demoError = classifyDemoError(result.error, requestUrl);
      if (demoError === "blocked") {
        // 別タブ等で localStorage フラグが無いまま書き込みが飛んだ場合の保険
        emitDemoBlocked();
      } else if (demoError === "expired") {
        // リンク失効: セッションを破棄して失効画面（/demo/:token）へ誘導する。
        // RequireAuth の 401 ハンドリング（/signin への SPA 遷移）と競合する
        // ため、イベント通知ではなくここで直接フルリロード遷移させる。
        const demo = getDemoMode();
        clearDemoMode();
        removeAuthTokenFromCookies();
        window.location.replace(demo ? `/demo/${demo.token}` : "/signin");
      }
    }
    return result;
  };
};
